import { Button, Table, Typography, notification, Modal, Input, Select, Tooltip, Descriptions, Tag } from "antd";
import { EyeOutlined, CloseCircleOutlined, CheckCircleOutlined, WarningOutlined, SearchOutlined } from '@ant-design/icons';
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import ajaxService from "../../../services/ajax-service";
import moment from "moment";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";
import { useTableHover } from "../../../hooks/useTableHover";

const { Text, Title } = Typography;

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'orange';
    case 'reviewed':
      return 'blue';
    case 'resolved':
      return 'green';
    case 'dismissed':
      return 'default';
    default:
      return 'default';
  }
};

// View Report Modal Component
const ViewReportModal = ({
  visible,
  onCancel,
  report,
  onStatusUpdate,
}) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(report?.status || '');

  useEffect(() => {
    if (report) {
      setCurrentStatus(report.status || '');
    }
  }, [report]);

  const handleStatusChange = async (newStatus) => {
    if (!report || newStatus === currentStatus) return;

    setUpdatingStatus(true);
    try {
      const response = await ajaxService.put(`/report-user/`, report.id, {
        status: newStatus
      });

      if (response.success) {
        setCurrentStatus(newStatus);
        notification.open({
          message: response.message || 'Report status updated successfully',
          icon: <CheckCircleOutlined style={{ color: 'var(--success-green)' }} />,
        });
        if (onStatusUpdate) {
          onStatusUpdate(response.data);
        }
      } else {
        notification.open({
          message: response.message || 'Failed to update report status',
          icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />,
        });
      }
    } catch (error) {
      console.error('Error updating report status:', error);
      notification.open({
        message: 'Failed to update report status',
        icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />,
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!report) return null;

  return (
    <Modal
      className="modal-overlay"
      width={calculateModalWidth('50%')}
      open={visible}
      maskClosable={false}
      onCancel={onCancel}
      footer={null}
      centered
    >
      <div style={{
        padding: "var(--spacing-2xl) var(--spacing-xl)",
        background: 'var(--dark-bg-card)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <div style={{
          marginBottom: 'var(--spacing-xl)',
          textAlign: "center",
          paddingBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
        }}>
          <Title id="modal-title" level={3} style={{
            color: 'var(--text-white)',
            margin: 0,
            fontFamily: 'Poppins-Bold',
            fontSize: '1.5rem'
          }}>
            Report Details
          </Title>
        </div>

        <Descriptions
          bordered
          column={1}
          size="middle"
          labelStyle={{
            color: 'var(--text-white)',
            fontWeight: 600,
            background: 'var(--dark-bg-hover)'
          }}
          contentStyle={{
            color: 'var(--text-gray)',
            background: 'var(--dark-bg-card)'
          }}
        >
          <Descriptions.Item label="Report ID">
            {report.id}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
              <Select
                value={currentStatus}
                onChange={handleStatusChange}
                disabled={updatingStatus}
                style={{ width: '200px' }}
                className="input-field"
              >
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="reviewed">Reviewed</Select.Option>
                <Select.Option value="resolved">Resolved</Select.Option>
                <Select.Option value="dismissed">Dismissed</Select.Option>
              </Select>
              <Tag color={getStatusColor(currentStatus)}>
                {currentStatus?.toUpperCase() || 'N/A'}
              </Tag>
              {updatingStatus && (
                <Text style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>
                  Updating...
                </Text>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Reason">
            {report.reason || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Reported User">
            {report.reported_user ? (
              <div>
                <Text strong style={{ color: 'var(--text-white)' }}>
                  {report.reported_user.name}
                </Text>
                <br />
                <Text style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>
                  {report.reported_user.email}
                </Text>
                {report.reported_user.id && (
                  <>
                    <br />
                    <Text style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>
                      ID: {report.reported_user.id}
                    </Text>
                  </>
                )}
              </div>
            ) : (
              <Text>User ID: {report.reported_user_id}</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Reporter">
            {report.reporter ? (
              <div>
                <Text strong style={{ color: 'var(--text-white)' }}>
                  {report.reporter.name}
                </Text>
                <br />
                <Text style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>
                  {report.reporter.email}
                </Text>
                {report.reporter.id && (
                  <>
                    <br />
                    <Text style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>
                      ID: {report.reporter.id}
                    </Text>
                  </>
                )}
              </div>
            ) : (
              <Text>User ID: {report.reporter_id}</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {report.created_at ? moment(report.created_at).format('DD-MMM-YYYY HH:mm:ss') : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {report.updated_at ? moment(report.updated_at).format('DD-MMM-YYYY HH:mm:ss') : 'N/A'}
          </Descriptions.Item>
        </Descriptions>

        <div style={{
          textAlign: "center",
          marginTop: 'var(--spacing-xl)',
          paddingTop: 'var(--spacing-lg)',
          borderTop: '1px solid rgba(254, 2, 125, 0.2)',
        }}>
          <Button
            onClick={onCancel}
            style={{
              minWidth: '120px',
              height: '40px',
              background: 'var(--dark-bg-hover)',
              borderColor: 'rgba(254, 2, 125, 0.3)',
              color: 'var(--text-white)'
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const Reports = () => {
  // Use custom table hover hook to prevent fixed column overlap
  useTableHover();

  const [showModal, setShowModal] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);

  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const debounceTimer = useRef(null);
  const isMounted = useRef(true);

  const [dataSource, setDataSource] = useState({ data: [], current_page: 1, per_page: 15, total: 0 });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const buildQueryParams = (status, reportedUserId, page = null, limit = 15) => {
    let params = [];
    if (status && status !== 'all') {
      params.push(`status=${status}`);
    }
    if (reportedUserId) {
      params.push(`reported_user_id=${reportedUserId}`);
    }
    if (limit) {
      params.push(`limit=${limit}`);
    }
    if (page !== null) {
      params.push(`page=${page}`);
    }
    return params.length > 0 ? `?${params.join('&')}` : '';
  };

  const handleSearch = useCallback(async ({ reported_user_id }) => {
    const searchId = reported_user_id ? reported_user_id.trim() : '';
    const queryParams = buildQueryParams(statusFilter, searchId, null);
    const response = await ajaxService.get(`/report-user${queryParams}`);

    const { success, data } = response;
    if (success && isMounted.current) {
      if (data.data && data.data.length === 0 && searchId) {
        setFilter('');
        notification.open({
          message: `Reported User ID ${searchId} not found!`,
          icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />
        });
      } else {
        setFilter(searchId);
      }

      // Transform response to match user module structure
      setDataSource({
        data: data.data || [],
        current_page: data.pagination?.current_page || 1,
        per_page: data.pagination?.per_page || 15,
        total: data.pagination?.total || 0,
      });
    }
  }, [statusFilter]);

  // Debounced search handler
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (searchValue !== undefined) {
      debounceTimer.current = setTimeout(() => {
        handleSearch({ reported_user_id: searchValue });
      }, 500);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue, statusFilter, handleSearch]);

  const handleChangePage = async (page) => {
    const queryParams = buildQueryParams(statusFilter, filter, page);
    const response = await ajaxService.get(`/report-user${queryParams}`);

    if (response.success) {
      const data = response.data;
      setDataSource({
        data: data.data || [],
        current_page: data.pagination?.current_page || page,
        per_page: data.pagination?.per_page || 15,
        total: data.pagination?.total || 0,
      });
    }
  };

  const initialize = useCallback(async () => {
    const queryParams = buildQueryParams(statusFilter, filter, null);
    const response = await ajaxService.get(`/report-user${queryParams}`);

    if (response.success && isMounted.current) {
      const data = response.data;
      setDataSource({
        data: data.data || [],
        current_page: data.pagination?.current_page || 1,
        per_page: data.pagination?.per_page || 15,
        total: data.pagination?.total || 0,
      });
    }
  }, [statusFilter, filter]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const openViewModal = useCallback((record) => {
    setViewingReport(record);
    setShowModal(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setViewingReport(null);
  }, []);

  const handleStatusUpdate = useCallback(async (updatedReport) => {
    // Update the viewing report if it's the same one
    if (viewingReport && viewingReport.id === updatedReport.id) {
      setViewingReport(updatedReport);
    }
    // Reload the listing API to refresh all data and statistics
    await initialize();
  }, [viewingReport, initialize]);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  // Calculate table scroll width using standardized utility
  // Table columns total: 80 + 200 + 200 + 300 + 120 + 180 + 100 = 1180px
  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 800, 1180, 40);
  }, [sidebarCollapsed]);

  const [tableWidth, setTableWidth] = useState(() => calculateTableScrollX(false, 800, 1180, 40));

  // Recalculate on window resize and sidebar toggle
  useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth());
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial calculation
    
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateTableWidth]);

  const columns = useMemo(() => [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 80,
      fixed: 'left',
      render: (text, record, index) => (
        <Text style={{ fontSize: '0.85rem' }}>
          {((dataSource.current_page || 1) - 1) * (dataSource.per_page || 15) + index + 1}
        </Text>
      ),
    },
    {
      title: "Reported User",
      key: "reported_user",
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <div>
          {record.reported_user ? (
            <>
              <Text style={{ fontSize: '0.85rem', display: 'block' }}>
                {record.reported_user.name || 'N/A'}
              </Text>
              <Text style={{ fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                {record.reported_user.email || ''}
              </Text>
            </>
          ) : (
            <Text style={{ fontSize: '0.85rem' }}>
              User ID: {record.reported_user_id}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Reporter",
      key: "reporter",
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <div>
          {record.reporter ? (
            <>
              <Text style={{ fontSize: '0.85rem', display: 'block' }}>
                {record.reporter.name || 'N/A'}
              </Text>
              <Text style={{ fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                {record.reporter.email || ''}
              </Text>
            </>
          ) : (
            <Text style={{ fontSize: '0.85rem' }}>
              User ID: {record.reporter_id}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 300,
      ellipsis: true,
      render: (reason) => (
        <Text style={{ fontSize: '0.85rem' }}>
          {reason || 'N/A'}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Reviewed', value: 'reviewed' },
        { text: 'Resolved', value: 'resolved' },
        { text: 'Dismissed', value: 'dismissed' },
      ],
      onFilter: (value, record) => record.status?.toLowerCase() === value.toLowerCase(),
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
      sortDirections: ['ascend', 'descend'],
      render: (createdAt) => (
        <Text style={{ fontSize: '0.85rem' }}>
          {createdAt ? moment(createdAt).format('DD-MMM-YYYY HH:mm') : 'N/A'}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                color: 'var(--primary-purple)',
                border: '1px solid var(--primary-purple)',
                borderRadius: 'var(--radius-md)',
                transition: 'var(--transition-fast)'
              }}
              icon={<EyeOutlined style={{ fontSize: '12px' }} />}
              onClick={() => openViewModal(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ], [dataSource, openViewModal]);

  // Calculate statistics
  const stats = useMemo(() => {
    const data = dataSource.data || [];
    return {
      total: dataSource.total || 0,
      pending: data.filter(r => r.status?.toLowerCase() === 'pending').length,
      reviewed: data.filter(r => r.status?.toLowerCase() === 'reviewed').length,
      resolved: data.filter(r => r.status?.toLowerCase() === 'resolved').length,
      dismissed: data.filter(r => r.status?.toLowerCase() === 'dismissed').length,
    };
  }, [dataSource]);

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <h1 className="module-title">User Reports Management</h1>
        <p className="module-subtitle">Manage and review user reports</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stats-card">
          <div className="stats-value">{stats.total || 0}</div>
          <div className="stats-label">Total Reports</div>
          <WarningOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{stats.pending || 0}</div>
          <div className="stats-label">Pending</div>
          <WarningOutlined style={{ color: 'orange', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{stats.reviewed || 0}</div>
          <div className="stats-label">Reviewed</div>
          <CheckCircleOutlined style={{ color: 'blue', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{stats.resolved || 0}</div>
          <div className="stats-label">Resolved</div>
          <CheckCircleOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-item">
            <Input
              placeholder="Search by Reported User ID..."
              prefix={<SearchOutlined style={{ color: 'var(--text-tertiary)' }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={(e) => {
                setSearchValue(e.target.value);
                handleSearch({ reported_user_id: e.target.value });
              }}
              className="input-field"
              allowClear
            />
          </div>
          <div className="filter-item">
            <Select
              placeholder="Filter by status"
              style={{ width: '100%' }}
              className="input-field"
              value={statusFilter}
              onChange={async (value) => {
                setStatusFilter(value);
                const searchId = searchValue ? searchValue.trim() : '';
                const queryParams = buildQueryParams(value, searchId, null);
                const response = await ajaxService.get(`/report-user${queryParams}`);
                if (response.success) {
                  const data = response.data;
                  setDataSource({
                    data: data.data || [],
                    current_page: data.pagination?.current_page || 1,
                    per_page: data.pagination?.per_page || 15,
                    total: data.pagination?.total || 0,
                  });
                }
              }}
            >
              <Select.Option value="all">All Status</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="reviewed">Reviewed</Select.Option>
              <Select.Option value="resolved">Resolved</Select.Option>
              <Select.Option value="dismissed">Dismissed</Select.Option>
            </Select>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Table
          columns={columns}
          dataSource={dataSource.data}
          className="data-table"
          rowKey='id'
          pagination={{
            onChange: (page, size) => handleChangePage(page),
            current: dataSource.current_page,
            pageSize: dataSource.per_page,
            total: dataSource.total,
            className: "data-table-pagination",
            showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} reports`,
            size: 'small'
          }}
          scroll={{ x: tableWidth }}
          size="small"
          bordered={false}
        />
      </div>

      <ViewReportModal
        visible={showModal}
        onCancel={handleCancel}
        report={viewingReport}
      />
    </div>
  );
};

export default Reports;
